/**
 * The arithmetic and the checks the dispatch form needs as you type.
 *
 * A deliberate second copy of what the API already enforces, which is the same
 * arrangement the daily register uses: the server is the authority and refuses a
 * bad dispatch whatever the screen believes, and this exists so a person is told
 * before they press Post rather than after. Both sides take their wording from
 * .docs/017-sales-and-dispatch.md, so the two messages read the same.
 *
 * Numbers are worked out with strings and `Number` rather than a decimal library,
 * because nothing here is stored — it is all shown and then thrown away. The net
 * weight that gets saved is computed again on the server with exact decimals.
 */

export const MAX_WEIGHT_KG = 100_000;
export const MAX_BIRDS = 100_000;

function numberOf(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * The net weight of a load, to three decimals.
 *
 * The sheet this replaces records a "first weight" and a "second weight" and does
 * not say which was the loaded one, because it does not need to: the net is the
 * difference either way.
 */
export function netWeightKg(first: string, second: string): string | null {
  const a = numberOf(first);
  const b = numberOf(second);
  if (a === null || b === null) return null;
  return Math.abs(b - a).toFixed(3);
}

/** Average live weight per bird, or null when there is nothing to divide. */
export function averageWeightKg(netKg: string | null, birds: string): string | null {
  const net = netKg === null ? null : numberOf(netKg);
  const count = numberOf(birds);
  if (net === null || count === null || count <= 0) return null;
  return (net / count).toFixed(3);
}

/** What is wrong with a pair of weighings, in plain English, or null. */
export function weightProblem(first: string, second: string): string | null {
  const given = [first, second].filter((v) => v.trim() !== '');
  if (given.length === 0) return null;
  if (given.length === 1) return 'Enter both weights, or neither';

  const a = numberOf(first);
  const b = numberOf(second);
  if (a === null || b === null) return 'A weight must be a number';
  if (a <= 0 || b <= 0) return 'A weight must be more than zero';
  if (a > MAX_WEIGHT_KG || b > MAX_WEIGHT_KG) {
    return `A weight cannot be more than ${MAX_WEIGHT_KG.toLocaleString('en-US')} kg`;
  }
  if (a === b) return 'The two weights are the same, so the load weighs nothing';
  return null;
}

export interface DispatchDraft {
  farmId: string;
  flockId: string;
  buyerId: string;
  issuedOn: string;
  birdCount: string;
  bookGatePassNo: string;
  firstWeightKg: string;
  secondWeightKg: string;
}

/**
 * Everything standing between a draft and being saved, keyed by field.
 *
 * Keyed rather than a list, so the form can put each message under the box it
 * belongs to instead of stacking them all at the top.
 */
export function draftProblems(
  draft: DispatchDraft,
  options: { birdsAlive?: number | null; today: string } = { today: '' },
): Record<string, string> {
  const problems: Record<string, string> = {};

  if (!draft.farmId) problems.farmId = 'Pick the farm';
  if (!draft.flockId) problems.flockId = 'Pick the flock';
  if (!draft.buyerId) problems.buyerId = 'Pick the party lifting the birds';
  if (!draft.issuedOn) problems.issuedOn = 'Enter the date';
  else if (options.today && draft.issuedOn > options.today) {
    problems.issuedOn = 'A dispatch cannot be dated in the future';
  }

  const birds = numberOf(draft.birdCount);
  if (birds === null) problems.birdCount = 'Enter how many birds were loaded';
  else if (birds <= 0) problems.birdCount = 'There has to be at least one bird';
  else if (!Number.isInteger(birds)) problems.birdCount = 'Birds are whole numbers';
  else if (birds > MAX_BIRDS) {
    problems.birdCount = `No lorry carries more than ${MAX_BIRDS.toLocaleString('en-US')} birds`;
  } else if (
    options.birdsAlive !== null &&
    options.birdsAlive !== undefined &&
    birds > options.birdsAlive
  ) {
    problems.birdCount =
      `This flock has ${options.birdsAlive.toLocaleString('en-US')} birds alive. ` +
      `You cannot dispatch ${birds.toLocaleString('en-US')}.`;
  }

  const weights = weightProblem(draft.firstWeightKg, draft.secondWeightKg);
  if (weights) problems.weights = weights;

  return problems;
}

/** What still has to happen before this dispatch can be posted. */
export function postingProblems(draft: DispatchDraft): string[] {
  const out: string[] = [];
  if (netWeightKg(draft.firstWeightKg, draft.secondWeightKg) === null) {
    out.push('Enter both weights before posting this dispatch');
  }
  if (!draft.bookGatePassNo.trim()) {
    out.push('Enter the gate pass number from the book before posting');
  }
  return out;
}

/** How a dispatch reads on a list: what it still needs, in one word. */
export type Stage = 'needsWeights' | 'needsPosting' | 'posted' | 'reversed';

export const STAGE_LABELS: Record<Stage, string> = {
  needsWeights: 'Needs weights',
  needsPosting: 'Needs posting',
  posted: 'Posted',
  reversed: 'Reversed',
};
